import React, { useMemo, useState, useContext, useEffect } from 'react';
import { Users, FileText, Activity, Calendar, AlertCircle, CheckCircle, Target, TrendingUp, Clock, Settings2, ChevronUp, ChevronDown, X, LayoutGrid, Mail, Info, TrendingDown, ClipboardList, Circle, Cake, RefreshCw } from 'lucide-react';
// import { 
//     BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, 
//     Cell, PieChart, Pie, LineChart, Line, AreaChart, Area, 
//     RadialBarChart, RadialBar, Legend
// } from 'recharts';
import { formatEuro } from '../../utils/templates';
import { BestandsModal } from '../../components/BestandsModal';
import { StatCard, DashboardHero, IOSCard, IOSButton, IOSBadge } from '../ui/custom';
import { ThemeContext } from '../../contexts/ThemeContext';
import { PageLayout, pageStyles } from '../PageLayout';
import { getOpenTasksCount, getRenewalsCount, getTodayShiftsCount, getGoalProgressPercentage } from '../../utils/dashboardMetrics';

const checkMatch = (allowed: any, c: any) => {
    const normalizeNumber = (v: any) => v ? String(v).replace(/[^0-9]/g, '').replace(/^0+/, '') : '';
    const normalizeName = (n: any) => n ? n.replace(/[:.;,]/g, ' ').replace(/\s+/g, ' ').trim() : '';

    if (!allowed || !Array.isArray(allowed)) return false;
    if (allowed.some(u => u.role === 'admin')) return true;
    return allowed.some(u => {
        if (!u) return false;
        const uAdv = normalizeNumber(u.adv), cAdp = normalizeNumber(c.adp), cBtnr = normalizeNumber(c.btnr), uBtnr = normalizeNumber(u.btnr);
        if (uAdv.length > 2 && ((cAdp && uAdv.includes(cAdp)) || (cBtnr && uAdv.includes(cBtnr)))) return true;
        if (uBtnr.length > 2 && ((cAdp && uBtnr.includes(cAdp)) || (cBtnr && uBtnr.includes(cBtnr)))) return true;
        const uName = normalizeName(u.name).toLowerCase(), cAdpName = normalizeName(c.adp).toLowerCase();
        return uName.length > 2 && cAdpName.includes(uName);
    });
};

const isBirthdayUpcoming = (d: any) => {
    const parseAnyDate = (s: any) => {
        if (!s) return null;
        if (typeof s === 'string' && s.includes('-')) {
            const [y, m, d] = s.split('-');
            return new Date(parseInt(y), parseInt(m) - 1, parseInt(d));
        }
        if (typeof s === 'string' && s.includes('.')) {
            const [d, m, y] = s.split('.');
            return new Date(parseInt(y), parseInt(m) - 1, parseInt(d));
        }
        return null;
    };

    const dob = parseAnyDate(d);
    if (!dob) return false;
    const today = new Date(), next = new Date(today.getFullYear(), dob.getMonth(), dob.getDate());
    if (next < today) next.setFullYear(today.getFullYear() + 1);
    const diff = Math.ceil((next.getTime() - today.getTime()) / (86400000));
    return diff >= 0 && diff <= 30;
};

const getNextBirthdayDate = (d: any) => {
    const parseAnyDate = (s: any) => {
        if (!s) return null;
        if (typeof s === 'string' && s.includes('-')) {
            const [y, m, d] = s.split('-');
            return new Date(parseInt(y), parseInt(m) - 1, parseInt(d));
        }
        if (typeof s === 'string' && s.includes('.')) {
            const [d, m, y] = s.split('.');
            return new Date(parseInt(y), parseInt(m) - 1, parseInt(d));
        }
        return null;
    };

    const dob = parseAnyDate(d);
    if (!dob) return new Date(9999, 11, 31);
    const today = new Date(), next = new Date(today.getFullYear(), dob.getMonth(), dob.getDate());
    if (next < today) next.setFullYear(today.getFullYear() + 1);
    return next;
};

const isExpiringSoon = (d: any) => {
    const parseAnyDate = (s: any) => {
        if (!s) return null;
        if (typeof s === 'string' && s.includes('-')) {
            const [y, m, d] = s.split('-');
            return new Date(parseInt(y), parseInt(m) - 1, parseInt(d));
        }
        if (typeof s === 'string' && s.includes('.')) {
            const [d, m, y] = s.split('.');
            return new Date(parseInt(y), parseInt(m) - 1, parseInt(d));
        }
        return null;
    };

    const expiry = parseAnyDate(d);
    if (!expiry) return false;
    const diff = Math.ceil((expiry.getTime() - new Date().getTime()) / (86400000));
    return diff > 0 && diff <= 90;
};

const NextShiftWidget = React.memo(({ shifts, currentUser, Clock: ClockProp, isDark }: any) => {
    const ClockToUse = ClockProp ?? Clock;
    const nextShift = useMemo(() => {
        if (!currentUser) return null;
        const todayStr = new Date().toISOString().split('T')[0];
        const myShifts = shifts.filter((s: any) => s.userId === currentUser.id && s.date >= todayStr).sort((a: any, b: any) => a.date.localeCompare(b.date));
        return myShifts[0];
    }, [shifts, currentUser]);

    if (!nextShift) return (
        <div className={`relative overflow-hidden p-4 sm:p-5 rounded-[1.5rem] flex items-center gap-4 glass-panel`}>
            <div className={`absolute inset-0 bg-gradient-to-br from-cyan-500/5 to-transparent pointer-events-none opacity-50`} />
            <div className={`relative w-12 h-12 rounded shadow-sm flex items-center justify-center flex-shrink-0 bg-slate-50 text-slate-400`}><ClockToUse size={22} strokeWidth={2} /></div>
            <div className="relative min-w-0"><p className={`text-xs font-medium text-muted-foreground mb-0.5`}>Nächster Dienst</p><p className={`text-base sm:text-lg font-bold text-card-foreground tracking-tight`}>Keine Schichten geplant</p></div>
        </div>
    );
    const date = new Date(nextShift.date);
    const dateDisplay = date.toLocaleDateString('de-DE', { weekday: 'long', day: '2-digit', month: 'long' });
    let colorClass = isDark ? "bg-blue-900 text-blue-300" : "bg-blue-50 text-blue-600", label = `${nextShift.start || ''} - ${nextShift.end || ''}`;
    if (nextShift.type === 'urlaub') { colorClass = isDark ? "bg-green-900 text-green-300" : "bg-green-50 text-green-600"; label = "Urlaub"; }
    if (nextShift.type === 'krank') { colorClass = isDark ? "bg-red-900 text-red-300" : "bg-red-50 text-red-600"; label = "Krank"; }
    if (nextShift.type === 'früh') label = "Frühschicht (09-13)";
    if (nextShift.type === 'spät') { colorClass = isDark ? "bg-indigo-900 text-indigo-300" : "bg-indigo-50 text-indigo-600"; label = "Spätschicht (13-18)"; }
    if (nextShift.type === 'freitag_frueh') { colorClass = isDark ? "bg-cyan-900 text-cyan-300" : "bg-cyan-50 text-cyan-600"; label = "FR Früh (09-12)"; }
    if (nextShift.type === 'freitag_spaet') { colorClass = isDark ? "bg-purple-900 text-purple-300" : "bg-purple-50 text-purple-600"; label = "FR Spät (12-15)"; }
    return (
        <div className={`relative overflow-hidden p-4 sm:p-5 md:p-6 rounded-[1.5rem] flex items-center gap-4 glass-panel`}>
            <div className={`absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent pointer-events-none opacity-50`} />
            <div className={`relative w-12 h-12 rounded shadow-sm flex items-center justify-center flex-shrink-0 ${colorClass}`}><ClockToUse size={22} strokeWidth={2} /></div>
            <div className="relative min-w-0 flex-1"><p className={`text-xs font-medium text-muted-foreground mb-0.5`}>Nächster Dienst · {dateDisplay}</p><p className={`text-base sm:text-lg md:text-xl font-bold text-card-foreground tracking-tight break-words`}>{label}</p></div>
        </div>
    );
});

const GoalWidget = React.memo(({ goals, currentUser, onEditGoal, isDark }: any) => {
    const currentGoal = useMemo(() => {
        if (!currentUser || !goals) return null;
        const currentYear = new Date().getFullYear();
        return goals.find((g: any) => g.userId === currentUser.id && g.year === currentYear);
    }, [goals, currentUser]);

    if (!currentGoal) return null;

    const progress = Math.round((currentGoal.current / currentGoal.target) * 100);
    const remaining = currentGoal.target - currentGoal.current;

    let colorClass = isDark ? "bg-green-900 text-green-300" : "bg-green-50 text-green-600";
    let progressColor = isDark ? "bg-green-500" : "bg-green-500";
    if (progress < 50) {
        colorClass = isDark ? "bg-red-900 text-red-300" : "bg-red-50 text-red-600";
        progressColor = isDark ? "bg-red-500" : "bg-red-500";
    } else if (progress < 75) {
        colorClass = isDark ? "bg-orange-900 text-orange-300" : "bg-orange-50 text-orange-600";
        progressColor = isDark ? "bg-orange-500" : "bg-orange-500";
    } else if (progress < 100) {
        colorClass = isDark ? "bg-blue-900 text-blue-300" : "bg-blue-50 text-blue-600";
        progressColor = isDark ? "bg-blue-500" : "bg-blue-500";
    }

    return (
        <div className={`glass-panel p-5 rounded-[1.5rem] relative`}>
            <button
                onClick={() => onEditGoal(currentUser, currentGoal)}
                className={`absolute top-4 right-4 p-2 rounded-full transition-colors ${isDark ? 'text-slate-400 hover:text-green-300' : 'text-gray-400 hover:text-green-500'}`}
                title="Ziel aktualisieren"
            >
                <Target size={20} />
            </button>
            <div className="flex items-center justify-between mb-4 pr-10">
                <div className="flex items-center gap-4">
                    <div className={`w-12 h-12 rounded shadow-sm flex items-center justify-center ${colorClass}`}>
                        <TrendingUp size={24} strokeWidth={2} />
                    </div>
                    <div>
                        <p className={`text-xs font-medium text-slate-500`}>Jahresziel {currentGoal.year}</p>
                        <p className={`text-2xl font-bold text-slate-900 tracking-tight`}>{progress}% erreicht</p>
                    </div>
                </div>
                <div className="text-right">
                    <p className={`text-xs text-slate-500 mb-1`}>Noch zu erreichen</p>
                    <p className={`text-lg font-bold text-slate-700`}>{remaining.toLocaleString('de-DE')} WS</p>
                </div>
            </div>

            <div className="space-y-2">
                <div className={`bg-slate-100 rounded-full h-4 overflow-hidden`}>
                    <div
                        className={`h-full transition-all duration-500 ${progressColor}`}
                        style={{ width: `${Math.min(progress, 100)}%` }}
                    />
                </div>
                <div className={`flex justify-between text-xs text-slate-500`}>
                    <span>Aktuell: <span className={`font-bold text-slate-700`}>{currentGoal.current.toLocaleString('de-DE')} WS</span></span>
                    <span>Ziel: <span className={`font-bold text-slate-700`}>{currentGoal.target.toLocaleString('de-DE')} WS</span></span>
                </div>
                {currentGoal.description && (
                    <p className={`text-xs mt-2 italic ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>{currentGoal.description}</p>
                )}
            </div>
        </div>
    );
});

// Jahresziel als Tabler Donut Chart
const GoalDonutWidget = React.memo(({ goal, currentUser, isDark }: any) => {
    if (!goal || !currentUser) return null;
    const progress = Math.min(100, (goal.current / goal.target) * 100);
    const progressRounded = Math.round(progress);
    const remaining = Math.max(0, goal.target - goal.current);

    // Tabler Widget Donut Props
    const circumference = 2 * Math.PI * 45;
    const strokeDash = (progress / 100) * circumference;

    const today = new Date();
    const year = today.getFullYear();
    const startOfYear = new Date(year, 0, 0).getTime();
    const dayOfYear = Math.max(1, Math.floor((today.getTime() - startOfYear) / 86400000));
    const daysInYear = (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0 ? 366 : 365;
    const targetWSByToday = goal.target > 0 ? Math.round((goal.target * dayOfYear) / daysInYear) : 0;
    const diffToSoll = (goal.current || 0) - targetWSByToday;
    const trendPercentage = targetWSByToday > 0 ? Math.round((diffToSoll / targetWSByToday) * 100) : 0;

    const progressTheme = progress >= 100
        ? { ring: 'stroke-emerald-500', label: 'text-emerald-500' }
        : progress >= 75
            ? { ring: 'stroke-emerald-400', label: 'text-emerald-400' }
            : progress >= 50
                ? { ring: 'stroke-amber-400', label: 'text-amber-500' }
                : { ring: 'stroke-blue-600', label: 'text-blue-600' };

    return (
        <div className={`relative flex flex-col glass-panel rounded-[1.5rem] p-4 sm:p-5 h-full`}>
            {/* Tabler Title & Value Header */}
            <div className="flex justify-between items-center mb-2">
                <h3 className="text-[10px] font-bold tracking-widest uppercase text-muted-foreground">Jahresziel {goal.year}</h3>
            </div>
            <div className="flex items-baseline gap-2 mb-2">
                <span className="text-3xl font-bold tracking-tight text-card-foreground leading-none">{goal.current.toLocaleString('de-DE')}</span>
                <span className={`text-sm font-semibold ${trendPercentage >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                    {trendPercentage > 0 ? '+' : ''}{trendPercentage}%
                    <TrendingUp size={14} className={`inline ml-1 mb-0.5 ${trendPercentage >= 0 ? '' : 'rotate-180 text-red-500'}`} />
                </span>
            </div>
            <p className="text-[11px] text-muted-foreground mb-4">
                Ziel: {goal.target.toLocaleString('de-DE')} WS <span className="mx-1">•</span> Noch {remaining.toLocaleString('de-DE')} WS
            </p>

            {/* Tabler Centered Donut */}
            <div className="flex-1 flex items-center justify-center mt-2 min-h-[140px]">
                <div className="relative w-32 h-32 sm:w-36 sm:h-36">
                    <svg viewBox="0 0 100 100" className="-rotate-90 w-full h-full">
                        <circle cx="50" cy="50" r="45" fill="none" strokeWidth="8" className="stroke-slate-100" />
                        <circle cx="50" cy="50" r="45" fill="none" strokeWidth="8" strokeLinecap="round" className={progressTheme.ring} strokeDasharray={circumference} strokeDashoffset={circumference - strokeDash} style={{ transition: 'stroke-dashoffset 0.8s ease-out' }} />
                    </svg>
                    <span className={`absolute inset-0 flex items-center justify-center text-2xl font-bold tabular-nums text-slate-900 dark:text-white`}>
                        {progressRounded}%
                    </span>
                </div>
            </div>
        </div>
    );
});

// Automatische Dashboard-Variante je nach Theme
const DashboardView = React.memo((props: any) => {
    const theme = useContext(ThemeContext);
    const isDark = theme?.isDark ?? false;
    return <DashboardViewBase {...props} isDark={isDark} />;
});

export default DashboardView;

// Gemeinsame Logik, Modus über Prop



const WIDGET_LABELS: Record<string, string> = {
    customers: 'Kunden',
    contracts: 'Verträge',
    birthdays: 'Geburtstage',
    expirations: 'Abläufe',
    tasks: 'Aufgaben',
    bestand: 'Bestand',
    nextShift: 'Nächster Dienst',
    reminders: 'Erinnerungen',
    verlaengerung: 'Verlängerung',
};

const VERLAENGERUNG_BACKEND_URL = (typeof import.meta !== 'undefined' ? ((import.meta as any).env?.DEV ? '' : ((import.meta as any).env?.VITE_VERLAENGERUNG_BACKEND_URL || '')) : '');
const VERLAENGERUNG_API_SECRET = (typeof import.meta !== 'undefined' && (import.meta as any).env?.VITE_VERLAENGERUNG_API_SECRET) || '';
const VERLAENGERUNG_AKTIV = Boolean(((typeof import.meta !== 'undefined' && (import.meta as any).env?.DEV) || VERLAENGERUNG_BACKEND_URL) && VERLAENGERUNG_API_SECRET);

const DEFAULT_WIDGET_ORDER = ['customers', 'contracts', 'birthdays', 'expirations', 'verlaengerung', 'tasks', 'bestand', 'nextShift', 'reminders'];
const WIDGET_PRESETS: Record<'vertrieb' | 'service' | 'leitung', string[]> = {
    vertrieb: ['tasks', 'customers', 'verlaengerung', 'expirations', 'contracts', 'bestand', 'nextShift', 'birthdays', 'reminders'],
    service: ['tasks', 'expirations', 'customers', 'birthdays', 'verlaengerung', 'nextShift', 'contracts', 'reminders', 'bestand'],
    leitung: ['bestand', 'contracts', 'tasks', 'expirations', 'verlaengerung', 'customers', 'nextShift', 'birthdays', 'reminders'],
};

const hasEmail = (c: any) => ((c?.mail || c?.email || '').trim() !== '');
const parseDashboardDate = (s: any) => {
    if (!s) return null;
    if (typeof s === 'string' && s.includes('-')) {
        const [y, m, d] = s.split('-');
        return new Date(parseInt(y), parseInt(m) - 1, parseInt(d));
    }
    if (typeof s === 'string' && s.includes('.')) {
        const [d, m, y] = s.split('.');
        return new Date(parseInt(y), parseInt(m) - 1, parseInt(d));
    }
    return null;
};
const formatDateSafe = (s: any) => {
    const d = parseDashboardDate(s);
    if (!d || isNaN(d.getTime())) return s || '–';
    return d.toLocaleDateString('de-DE');
};
const formatMoneySafe = (v: any) => {
    if (v == null || v === '') return '–';
    const n = Number(String(v).replace(/\./g, '').replace(',', '.').replace(/[^\d.-]/g, ''));
    if (Number.isNaN(n)) return String(v);
    return `${n.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
};

const DashboardViewBase = React.memo(({ customers, tasks, shifts, currentUser, goals, onSelectCustomer, onUpdateTask, onDeleteTask, onShowTaskModal, onOpenBirthdays, onOpenExpirations, allUsers, contractStates, onToggleContractState, onOpenTasks, onOpenCustomers, onEditGoal, Clock, onNavigateTo, isDark, adminView, setAdminView, widgetOrder, onSaveWidgetOrder }: any) => {
    const [showBestandsModal, setShowBestandsModal] = useState(false);
    const [showWidgetModal, setShowWidgetModal] = useState(false);
    const [editOrder, setEditOrder] = useState<string[]>([]);
    const [bestaetigungenCount, setBestaetigungenCount] = useState(0);
    const [tasksTab, setTasksTab] = useState<'open' | 'week' | 'overdue'>('open');
    const [renewalRange, setRenewalRange] = useState<7 | 30 | 90>(30);
    const [contextRange, setContextRange] = useState<7 | 30 | 90>(30);
    const [density, setDensity] = useState<'comfort' | 'compact'>('comfort');
    const [scanMode, setScanMode] = useState(false);
    const order = (widgetOrder && widgetOrder.length) ? widgetOrder : DEFAULT_WIDGET_ORDER;

    useEffect(() => {
        if (!VERLAENGERUNG_AKTIV || !currentUser) return;
        const url = new URL('/api/bestaetigungen', VERLAENGERUNG_BACKEND_URL || window.location.origin);
        url.searchParams.set('apiSecret', VERLAENGERUNG_API_SECRET);
        if (currentUser.role !== 'admin' && currentUser.name) url.searchParams.set('zustMA', currentUser.name);
        fetch(url.toString())
            .then((r) => {
                if (!r.ok) return { items: [] };
                return r.json();
            })
            .then((data) => setBestaetigungenCount((data.items || []).length))
            .catch(() => setBestaetigungenCount(0));
    }, [VERLAENGERUNG_AKTIV, currentUser?.id, currentUser?.role, currentUser?.name]);

    const currentGoal = useMemo(() => {
        if (!currentUser || !goals) return null;
        const currentYear = new Date().getFullYear();
        return goals.find((g: any) => g.userId === currentUser.id && g.year === currentYear);
    }, [goals, currentUser]);

    // Trend-Berechnung für den DashboardHero (Soll-Ist-Abgleich bis heute)
    const performanceTrend = useMemo(() => {
        if (!currentGoal || !currentGoal.target) return 0;
        const today = new Date();
        const year = today.getFullYear();
        const startOfYear = new Date(year, 0, 0).getTime();
        const dayOfYear = Math.max(1, Math.floor((today.getTime() - startOfYear) / 86400000));
        const daysInYear = (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0 ? 366 : 365;

        const targetWSByToday = (currentGoal.target * dayOfYear) / daysInYear;
        const diffToSoll = (currentGoal.current || 0) - targetWSByToday;

        return targetWSByToday > 0 ? Math.round((diffToSoll / targetWSByToday) * 100) : 0;
    }, [currentGoal]);
    // const [globalSearch, setGlobalSearch] = useState('');
    // Für die Ansicht: User-Objekt ggf. überschreiben
    let viewUser = currentUser;
    if (adminView === 'employee' && currentUser.role === 'admin') {
        viewUser = { ...currentUser, role: 'employee', allowedToView: currentUser.allowedToView && currentUser.allowedToView.length > 0 ? currentUser.allowedToView : [currentUser.id] };
    }

    const myCustomers = useMemo(() => {
        if (!viewUser) return [];
        // Zeige alle Kunden nur für Admins
        if (adminView === 'admin' && currentUser.role === 'admin') {
            return customers;
        }
        // Sonst nur eigene Kunden
        const allowedUserIds = [viewUser.id, ...(viewUser.allowedToView || [])];
        return customers.filter((c: any) => checkMatch(allUsers.filter((u: any) => allowedUserIds.includes(u.id)), c));
    }, [customers, viewUser, allUsers, adminView, currentUser]);

    const upcomingBirthdays = useMemo(() => myCustomers.filter((c: any) => isBirthdayUpcoming(c.gebDatum)).sort((a: any, b: any) => getNextBirthdayDate(a.gebDatum).getTime() - getNextBirthdayDate(b.gebDatum).getTime()), [myCustomers]);

    const expiringContractsCount = useMemo(() => {
        const parseAnyDate = (s: any) => {
            if (!s) return null;
            if (typeof s === 'string' && s.includes('-')) {
                const [y, m, d] = s.split('-');
                return new Date(parseInt(y), parseInt(m) - 1, parseInt(d));
            }
            if (typeof s === 'string' && s.includes('.')) {
                const [d, m, y] = s.split('.');
                return new Date(parseInt(y), parseInt(m) - 1, parseInt(d));
            }
            return null;
        };

        // Standardansicht wie im Modal: vom 01.01.2000 bis in die nächsten 5 Monate
        const defaultFrom = new Date(2000, 0, 1);
        const defaultTo = new Date();
        defaultTo.setMonth(defaultTo.getMonth() + 5);

        const isKfzOrLeben = (sparte: any) => {
            const s = (sparte || '').toLowerCase().trim();
            return /kfz|kraftfahrzeug|lebensversicherung|^leben$/.test(s) || s === 'lv';
        };
        let count = 0;
        myCustomers.forEach((c: any) => {
            (c.verträge || []).forEach((v: any) => {
                const safeId = (v.vsnr || '').replace(/\//g, '_');
                const expiryDate = parseAnyDate(v.ablauf);
                const matchesDateFilter = expiryDate && expiryDate >= defaultFrom && expiryDate <= defaultTo;
                if (contractStates[safeId] !== 'done' && matchesDateFilter && !isKfzOrLeben(v.sparte)) {
                    count++;
                }
            });
        });
        return count;
    }, [myCustomers, contractStates]);

    const nextExpirationsList = useMemo(() => {
        const parseAnyDate = (s: any) => {
            if (!s) return null;
            if (typeof s === 'string' && s.includes('-')) {
                const [y, m, d] = s.split('-');
                return new Date(parseInt(y), parseInt(m) - 1, parseInt(d));
            }
            if (typeof s === 'string' && s.includes('.')) {
                const [d, m, y] = s.split('.');
                return new Date(parseInt(y), parseInt(m) - 1, parseInt(d));
            }
            return null;
        };
        const isKfzOrLeben = (sparte: any) => {
            const s = (sparte || '').toLowerCase().trim();
            return /kfz|kraftfahrzeug|lebensversicherung|^leben$/.test(s) || s === 'lv';
        };
        const defaultTo = new Date();
        defaultTo.setMonth(defaultTo.getMonth() + 5);
        const today = new Date();
        const items: { customerName: string; sparte: string; ablauf: string; daysUntil: number }[] = [];
        myCustomers.forEach((c: any) => {
            (c.verträge || []).forEach((v: any) => {
                const safeId = (v.vsnr || '').replace(/\//g, '_');
                const expiryDate = parseAnyDate(v.ablauf);
                if (contractStates[safeId] === 'done' || !expiryDate || expiryDate < today || isKfzOrLeben(v.sparte)) return;
                const daysUntil = Math.ceil((expiryDate.getTime() - today.getTime()) / 86400000);
                items.push({
                    customerName: [c.vorname, c.name].filter(Boolean).join(' ') || c.adp || 'Kunde',
                    sparte: v.sparte || '',
                    ablauf: v.ablauf || '',
                    daysUntil
                });
            });
        });
        return items.sort((a, b) => a.daysUntil - b.daysUntil).slice(0, 7);
    }, [myCustomers, contractStates]);

    const verlaengerungOffenCount = useMemo(() => {
        if (!VERLAENGERUNG_AKTIV || !contractStates) return 0;
        const parseAnyDate = (s: any) => {
            if (!s) return null;
            if (typeof s === 'string' && s.includes('-')) {
                const [y, m, d] = s.split('-');
                return new Date(parseInt(y), parseInt(m) - 1, parseInt(d));
            }
            if (typeof s === 'string' && s.includes('.')) {
                const [d, m, y] = s.split('.');
                return new Date(parseInt(y), parseInt(m) - 1, parseInt(d));
            }
            return null;
        };
        const defaultFrom = new Date(2000, 0, 1);
        const defaultTo = new Date();
        defaultTo.setMonth(defaultTo.getMonth() + 5);
        const isKfzOrLeben = (sparte: any) => {
            const s = (sparte || '').toLowerCase().trim();
            return /kfz|kraftfahrzeug|lebensversicherung|^leben$/.test(s) || s === 'lv';
        };
        let count = 0;
        (myCustomers || []).forEach((c: any) => {
            if (!hasEmail(c)) return;
            (c.verträge || []).forEach((v: any) => {
                const safeId = (v.vsnr || '').replace(/\//g, '_');
                if (contractStates[safeId] === 'done') return;
                const expiryDate = parseAnyDate(v.ablauf);
                if (!expiryDate || expiryDate < defaultFrom || expiryDate > defaultTo) return;
                if (isKfzOrLeben(v.sparte)) return;
                count++;
            });
        });
        return count;
    }, [myCustomers, contractStates, VERLAENGERUNG_AKTIV]);

    const myTasks = (tasks || []).filter((t: any) => t.status !== 'done' && (currentUser?.role === 'admin' || t.assignedTo === currentUser?.name || t.assignedTo === 'Alle'));

    const totalContracts = useMemo(() => {
        return myCustomers.reduce((sum: any, c: any) => {
            return sum + (c.verträge?.length || 0);
        }, 0);
    }, [myCustomers]);


    // Bestand (Summe nur aus JAB – Jahresbeitrag)
    const bestandSum = useMemo(() => {
        const parseMoney = (val: any): number => {
            if (val == null) return 0;
            let str = String(val).replace(/[€$EUR\s]/gi, '').trim();
            if (!str) return 0;
            if (str.includes('.') && str.includes(',')) {
                if (str.lastIndexOf(',') > str.lastIndexOf('.')) {
                    str = str.replace(/\./g, '').replace(',', '.');
                } else {
                    str = str.replace(/,/g, '');
                }
            } else if (str.includes(',')) {
                str = str.replace(',', '.');
            } else if (str.includes('.')) {
                if (/\.\d{3}$/.test(str)) str = str.replace(/\./g, '');
            }
            const num = parseFloat(str);
            return isNaN(num) ? 0 : num;
        };
        let sum = 0;
        myCustomers.forEach((c: any) => {
            const vertraege = c.verträge ?? c.vertraege ?? [];
            vertraege.forEach((v: any) => {
                const jab = parseMoney(v.JAB ?? v.jab ?? v.beitrag ?? v.Beitrag ?? v.jahresbeitrag ?? v.Jahresbeitrag);
                sum += jab;
            });
        });
        return sum;
    }, [myCustomers]);

    // Debug-Ausgabe für Mitarbeiter-Ansicht
    React.useEffect(() => {
        if (adminView === 'employee') {
            // eslint-disable-next-line no-console
            console.log('[DEBUG] Mitarbeiter-Ansicht: viewUser', viewUser);
            // eslint-disable-next-line no-console
            console.log('[DEBUG] Gefundene Kunden:', myCustomers);
        }
    }, [adminView, viewUser, myCustomers]);

    // --- SaaS DASHBOARD KPI CALCULATIONS ---
    const renewalsInRangeCount = useMemo(() => getRenewalsCount(myCustomers, contextRange), [myCustomers, contextRange]);
    const todayShiftsCount = useMemo(() => getTodayShiftsCount(shifts), [shifts]);
    const goalPercentage = useMemo(() => getGoalProgressPercentage(currentGoal), [currentGoal]);
    const openOrdersCount = useMemo(
        () => (myTasks || []).filter((t: any) => ['open', 'in_progress'].includes((t.status || '').toLowerCase()) || (!t.completed && t.status !== 'done')).length,
        [myTasks],
    );
    const todayIso = useMemo(() => new Date().toISOString().split('T')[0], []);
    const weekEndIso = useMemo(() => {
        const d = new Date();
        d.setDate(d.getDate() + 7);
        return d.toISOString().split('T')[0];
    }, []);
    const tasksOpenRows = useMemo(
        () => (myTasks || []).filter((t: any) => !t.completed && t.status !== 'done'),
        [myTasks],
    );
    const tasksWeekRows = useMemo(
        () => (myTasks || []).filter((t: any) => !t.completed && t.status !== 'done' && t.dueDate && t.dueDate >= todayIso && t.dueDate <= weekEndIso),
        [myTasks, todayIso, weekEndIso],
    );
    const tasksOverdueRows = useMemo(
        () => (myTasks || []).filter((t: any) => !t.completed && t.status !== 'done' && t.dueDate && t.dueDate < todayIso),
        [myTasks, todayIso],
    );
    const callbacksOpenCount = useMemo(
        () =>
            (myTasks || []).filter((t: any) => {
                const text = `${t?.title || ''} ${t?.description || ''}`.toLowerCase();
                return (text.includes('rückruf') || text.includes('rueckruf')) && !t.completed && String(t.status || '').toLowerCase() !== 'done';
            }).length,
        [myTasks],
    );
    const renewals7DaysCount = useMemo(() => getRenewalsCount(myCustomers, 7), [myCustomers]);
    const taskRows = tasksTab === 'week' ? tasksWeekRows : tasksTab === 'overdue' ? tasksOverdueRows : tasksOpenRows;
    const renewalRows = useMemo(() => {
        const today = new Date();
        const res: Array<{ id: string; customerId: string; customerName: string; sparte: string; ablauf: string; daysUntil: number; beitrag: any; status: string }> = [];
        (myCustomers || []).forEach((c: any) => {
            (c.verträge || c.vertraege || []).forEach((v: any, idx: number) => {
                const exp = parseDashboardDate(v.ablauf);
                if (!exp) return;
                const daysUntil = Math.ceil((exp.getTime() - today.getTime()) / 86400000);
                if (daysUntil < 0 || daysUntil > renewalRange) return;
                const safeId = (v.vsnr || `${c.id}-${idx}`).replace(/\//g, '_');
                if (contractStates?.[safeId] === 'done') return;
                res.push({
                    id: safeId,
                    customerId: c.id,
                    customerName: [c.vorname, c.name].filter(Boolean).join(' ') || c.adp || 'Kunde',
                    sparte: v.sparte || v.produkt || '–',
                    ablauf: v.ablauf || '–',
                    daysUntil,
                    beitrag: v.beitrag ?? v.JAB ?? v.jab ?? v.jahresbeitrag ?? v.Jahresbeitrag,
                    status: daysUntil <= 7 ? 'In 7 Tagen' : daysUntil <= 30 ? 'In 30 Tagen' : 'In 90 Tagen',
                });
            });
        });
        return res.sort((a, b) => a.daysUntil - b.daysUntil).slice(0, 12);
    }, [myCustomers, renewalRange, contractStates]);
    const todayShiftRows = useMemo(
        () => (shifts || []).filter((s: any) => s.date === new Date().toISOString().split('T')[0]).slice(0, 4),
        [shifts],
    );
    const interactionTiles = useMemo(() => {
        const palette = [
            { bg: 'bg-[#2F6BFF]', text: 'text-white' },
            { bg: 'bg-[#53A5BE]', text: 'text-white' },
            { bg: 'bg-[#111111]', text: 'text-white' },
            { bg: 'bg-[#F3E74D]', text: 'text-slate-900' },
        ];
        const items = [
            ...tasksOpenRows.slice(0, 3).map((t: any) => ({
                title: t.title || t.name || 'Aufgabe',
                subtitle: t.customerName || t.customer || t.kunde || 'Kunde',
                value: t.dueDate ? formatDateSafe(t.dueDate) : 'Offen',
            })),
            ...renewalRows.slice(0, 3).map((r) => ({
                title: r.sparte || 'Verlängerung',
                subtitle: r.customerName,
                value: `${r.daysUntil} Tage`,
            })),
        ].slice(0, 4);
        return items.map((item, i) => ({ ...item, ...palette[i % palette.length] }));
    }, [tasksOpenRows, renewalRows]);
    const goToView = (viewId: string) => {
        if (typeof onNavigateTo === 'function') onNavigateTo(viewId);
    };
    const isCompact = density === 'compact';
    const tableCellPadding = scanMode ? 'py-2.5' : 'py-4';
    const tableHeaderPadding = scanMode ? 'py-2' : 'py-3';
    const applyContextRange = (range: 7 | 30 | 90) => {
        setContextRange(range);
        setRenewalRange(range);
    };
    const applyWidgetPreset = (preset: 'vertrieb' | 'service' | 'leitung') => {
        const nextOrder = WIDGET_PRESETS[preset];
        setEditOrder(nextOrder);
        if (onSaveWidgetOrder) onSaveWidgetOrder(nextOrder);
    };

    return (
        <PageLayout>
            <div className="animate-fade-in-up">
                <div className={`relative rounded-[24px] app-glass-panel ${isCompact ? 'p-3 sm:p-4' : 'p-4 sm:p-6'}`}>
                    <div className="sticky top-2 z-20 mb-4 rounded-2xl border border-white/50 dark:border-white/10 bg-white/70 dark:bg-slate-900/70 backdrop-blur-xl px-3 py-2 shadow-sm">
                        <div className="flex flex-wrap items-center justify-between gap-2">
                            <div className="flex items-center gap-2">
                                <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-300">Kontext</span>
                                <span className="rounded-full bg-slate-100 dark:bg-white/10 px-2 py-1 text-[11px] font-semibold text-slate-700 dark:text-slate-200">
                                    {new Date().toLocaleDateString('de-DE', { weekday: 'short', day: '2-digit', month: '2-digit' })}
                                </span>
                            </div>
                            <div className="flex items-center gap-2">
                                <div className="flex items-center gap-1 rounded-full bg-slate-100 dark:bg-white/10 p-1">
                                    <button onClick={() => setDensity('comfort')} className={`rounded-full px-3 py-1 text-[11px] font-semibold transition-colors ${density === 'comfort' ? 'bg-white text-slate-900 shadow-sm dark:bg-slate-800 dark:text-white' : 'text-slate-600 dark:text-slate-300'}`}>Komfort</button>
                                    <button onClick={() => setDensity('compact')} className={`rounded-full px-3 py-1 text-[11px] font-semibold transition-colors ${density === 'compact' ? 'bg-white text-slate-900 shadow-sm dark:bg-slate-800 dark:text-white' : 'text-slate-600 dark:text-slate-300'}`}>Kompakt</button>
                                </div>
                                <button
                                    onClick={() => setScanMode((prev) => !prev)}
                                    className={`rounded-full px-3 py-1 text-[11px] font-semibold transition-colors ${scanMode ? 'bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900' : 'bg-slate-100 text-slate-600 dark:bg-white/10 dark:text-slate-300'}`}
                                >
                                    Scanmodus
                                </button>
                                <IOSButton variant="secondary" className="px-3 py-2 text-xs" onClick={() => { setEditOrder(Array.from(new Set([...order, ...Object.keys(WIDGET_LABELS)]))); setShowWidgetModal(true); }}>
                                    Anpassen
                                </IOSButton>
                            </div>
                        </div>
                        <div className="mt-2 flex flex-wrap items-center gap-2">
                            <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-300">Zeitraum</span>
                            {[7, 30, 90].map((range) => (
                                <button
                                    key={range}
                                    onClick={() => applyContextRange(range as 7 | 30 | 90)}
                                    className={`rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${contextRange === range ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-600 dark:bg-white/10 dark:text-slate-200'}`}
                                >
                                    {range} Tage
                                </button>
                            ))}
                        </div>
                        <div className="mt-2 flex flex-wrap items-center gap-2">
                            <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-300">Preset</span>
                            <button onClick={() => applyWidgetPreset('vertrieb')} className="rounded-full bg-slate-100 px-3 py-1.5 text-xs font-semibold text-slate-700 dark:bg-white/10 dark:text-slate-200">Vertrieb</button>
                            <button onClick={() => applyWidgetPreset('service')} className="rounded-full bg-slate-100 px-3 py-1.5 text-xs font-semibold text-slate-700 dark:bg-white/10 dark:text-slate-200">Service</button>
                            <button onClick={() => applyWidgetPreset('leitung')} className="rounded-full bg-slate-100 px-3 py-1.5 text-xs font-semibold text-slate-700 dark:bg-white/10 dark:text-slate-200">Leitung</button>
                        </div>
                    </div>

                    <div className={`mb-5 flex flex-wrap items-center justify-between ${isCompact ? 'gap-2' : 'gap-3'}`}>
                        <div className="flex flex-wrap items-center gap-2">
                            <button onClick={() => goToView('customers')} className="rounded-full bg-slate-900 px-4 py-2 text-xs font-semibold text-white dark:bg-slate-100 dark:text-slate-900">Kunden</button>
                            <button onClick={() => goToView('tasks')} className="rounded-full app-glass-panel px-4 py-2 text-xs font-semibold text-slate-700 dark:text-slate-100">Aufgaben</button>
                            <button onClick={() => goToView('expirations')} className="rounded-full app-glass-panel px-4 py-2 text-xs font-semibold text-slate-700 dark:text-slate-100">Abläufe</button>
                            <button onClick={() => goToView('goals')} className="rounded-full app-glass-panel px-4 py-2 text-xs font-semibold text-slate-700 dark:text-slate-100">Ziele</button>
                            <button onClick={() => goToView('roster')} className="rounded-full app-glass-panel px-4 py-2 text-xs font-semibold text-slate-700 dark:text-slate-100">Dienstplan</button>
                        </div>
                        <div className="rounded-full app-glass-panel px-3 py-1.5 text-[11px] font-semibold text-slate-600 dark:text-slate-200">
                            CRM Arbeitsbereich
                        </div>
                    </div>

                    <div className={`mb-5 grid grid-cols-1 md:grid-cols-3 ${isCompact ? 'gap-2' : 'gap-3'}`}>
                        <div className="rounded-2xl border border-red-200/70 bg-red-50/70 dark:border-red-400/20 dark:bg-red-500/10 px-4 py-3">
                            <p className="text-[11px] font-semibold text-red-700 dark:text-red-300">Heute wichtig: Überfällige Aufgaben</p>
                            <p className="mt-1 text-xl font-bold text-red-800 dark:text-red-200">{tasksOverdueRows.length}</p>
                        </div>
                        <div className="rounded-2xl border border-amber-200/70 bg-amber-50/70 dark:border-amber-400/20 dark:bg-amber-500/10 px-4 py-3">
                            <p className="text-[11px] font-semibold text-amber-700 dark:text-amber-300">Heute wichtig: Abläufe in 7 Tagen</p>
                            <p className="mt-1 text-xl font-bold text-amber-800 dark:text-amber-200">{renewals7DaysCount}</p>
                        </div>
                        <div className="rounded-2xl border border-blue-200/70 bg-blue-50/70 dark:border-blue-400/20 dark:bg-blue-500/10 px-4 py-3">
                            <p className="text-[11px] font-semibold text-blue-700 dark:text-blue-300">Heute wichtig: Offene Rückrufe</p>
                            <p className="mt-1 text-xl font-bold text-blue-800 dark:text-blue-200">{callbacksOpenCount}</p>
                        </div>
                    </div>

                    <div className={`mb-5 grid grid-cols-2 lg:grid-cols-4 ${isCompact ? 'gap-2' : 'gap-3'}`}>
                        <button onClick={() => goToView('tasks')} className="rounded-2xl app-glass-panel px-4 py-3 text-left hover:bg-white/30 transition-colors">
                            <div className="text-[11px] font-semibold text-slate-600 dark:text-slate-300">Offene Aufgaben</div>
                            <div className="mt-1 text-2xl font-bold text-slate-900 dark:text-white">{openOrdersCount}</div>
                        </button>
                        <button onClick={() => goToView('expirations')} className="rounded-2xl app-glass-panel px-4 py-3 text-left hover:bg-white/30 transition-colors">
                            <div className="text-[11px] font-semibold text-slate-600 dark:text-slate-300">Abläufe ({contextRange} Tage)</div>
                            <div className="mt-1 text-2xl font-bold text-slate-900 dark:text-white">{renewalsInRangeCount}</div>
                        </button>
                        <button onClick={() => goToView('roster')} className="rounded-2xl app-glass-panel px-4 py-3 text-left hover:bg-white/30 transition-colors">
                            <div className="text-[11px] font-semibold text-slate-600 dark:text-slate-300">Heutige Termine</div>
                            <div className="mt-1 text-2xl font-bold text-slate-900 dark:text-white">{todayShiftsCount}</div>
                        </button>
                        <button onClick={() => goToView('goals')} className="rounded-2xl app-glass-panel px-4 py-3 text-left hover:bg-white/30 transition-colors">
                            <div className="text-[11px] font-semibold text-slate-600 dark:text-slate-300">Zielerreichung</div>
                            <div className="mt-1 text-2xl font-bold text-slate-900 dark:text-white">{goalPercentage}%</div>
                        </button>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
                        <div className="hidden xl:flex lg:col-span-1 justify-center">
                            <div className="sticky top-24 h-fit rounded-[22px] app-glass-panel p-2">
                                <button onClick={() => goToView('dashboard')} className="mb-2 h-10 w-10 rounded-xl border border-blue-200 bg-blue-50 text-blue-700 dark:border-blue-300/30 dark:bg-blue-500/15 dark:text-blue-300 flex items-center justify-center"><LayoutGrid size={16} /></button>
                                <button onClick={() => goToView('customers')} className="mb-2 h-10 w-10 rounded-xl bg-slate-100 text-slate-600 dark:bg-white/10 dark:text-slate-200 flex items-center justify-center"><Users size={16} /></button>
                                <button onClick={() => goToView('tasks')} className="mb-2 h-10 w-10 rounded-xl bg-slate-100 text-slate-600 dark:bg-white/10 dark:text-slate-200 flex items-center justify-center"><ClipboardList size={16} /></button>
                                <button onClick={() => goToView('expirations')} className="mb-2 h-10 w-10 rounded-xl bg-slate-100 text-slate-600 dark:bg-white/10 dark:text-slate-200 flex items-center justify-center"><RefreshCw size={16} /></button>
                                <button onClick={() => goToView('phonebook')} className="h-10 w-10 rounded-xl bg-slate-100 text-slate-600 dark:bg-white/10 dark:text-slate-200 flex items-center justify-center"><Mail size={16} /></button>
                            </div>
                        </div>

                        {/* ZONE B: Main Work Area (Left) */}
                        <div className="lg:col-span-7 flex flex-col gap-6">
                            <IOSCard title="Interaktionsverlauf" className="border border-blue-200/60 dark:border-blue-400/20">
                                <div className={`grid grid-cols-1 sm:grid-cols-2 ${isCompact ? 'gap-2' : 'gap-3'}`}>
                                    {interactionTiles.map((tile, idx) => (
                                        <button key={`${tile.title}-${idx}`} onClick={() => goToView('tasks')} className={`rounded-2xl p-4 text-left transition-colors hover:brightness-95 ${tile.bg} ${tile.text}`}>
                                            <p className="text-[11px] opacity-80 font-semibold">{tile.subtitle}</p>
                                            <p className="mt-1 text-base font-semibold leading-tight">{tile.title}</p>
                                            <p className={`mt-3 text-sm font-bold ${tile.text}`}>{tile.value}</p>
                                        </button>
                                    ))}
                                </div>
                            </IOSCard>

                        {/* B1: Aufgaben & Aufträge */}
                            <IOSCard
                                title="Aufgaben & Aufträge"
                                className="border border-blue-200/60 dark:border-blue-400/20"
                                headerAction={
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <button onClick={() => setTasksTab('open')} className={`px-3 py-1.5 rounded-full text-xs font-semibold ${tasksTab === 'open' ? 'bg-[#EEF3FF] text-[#2F6BFF]' : 'bg-[#F3F5F9] text-slate-500'}`}>Offen</button>
                                        <button onClick={() => setTasksTab('week')} className={`px-3 py-1.5 rounded-full text-xs font-semibold ${tasksTab === 'week' ? 'bg-[#EEF3FF] text-[#2F6BFF]' : 'bg-[#F3F5F9] text-slate-500'}`}>Diese Woche</button>
                                        <button onClick={() => setTasksTab('overdue')} className={`px-3 py-1.5 rounded-full text-xs font-semibold ${tasksTab === 'overdue' ? 'bg-[#FEECEC] text-red-600' : 'bg-[#F3F5F9] text-slate-500'}`}>Überfällig</button>
                                    </div>
                                }
                            >
                            <div className="overflow-x-auto -mx-6">
                                <table className="w-full text-left border-collapse">
                                    <thead>
                                        <tr className={`bg-slate-50 border-y border-[#E9ECF2] ${scanMode ? 'sticky top-0 z-10' : ''}`}>
                                            <th className={`px-4 sm:px-6 ${tableHeaderPadding} text-[10px] font-bold uppercase tracking-widest text-slate-400`}>Titel</th>
                                            <th className={`hidden sm:table-cell px-4 sm:px-6 ${tableHeaderPadding} text-[10px] font-bold uppercase tracking-widest text-slate-400`}>Kunde</th>
                                            <th className={`px-4 sm:px-6 ${tableHeaderPadding} text-[10px] font-bold uppercase tracking-widest text-slate-400`}>Fällig am</th>
                                            <th className={`hidden md:table-cell px-4 sm:px-6 ${tableHeaderPadding} text-[10px] font-bold uppercase tracking-widest text-slate-400`}>Priorität</th>
                                            <th className={`px-4 sm:px-6 ${tableHeaderPadding} text-[10px] font-bold uppercase tracking-widest text-slate-400`}>Status</th>
                                            <th className={`hidden lg:table-cell px-4 sm:px-6 ${tableHeaderPadding} text-[10px] font-bold uppercase tracking-widest text-slate-400`}>Verantwortlich</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-[#E9ECF2]">
                                        {taskRows.slice(0, 8).map((task: any) => {
                                            const due = task.dueDate || task.deadline;
                                            const overdue = Boolean(due && due < todayIso);
                                            const rawStatus = String(task.status || '').toLowerCase();
                                            const statusText = overdue
                                                ? 'Überfällig'
                                                : rawStatus === 'in_progress'
                                                    ? 'In Arbeit'
                                                    : rawStatus === 'done'
                                                        ? 'Erledigt'
                                                        : 'Offen';
                                            return (
                                                <tr key={task.id} className="hover:bg-slate-50/50 transition-colors cursor-pointer" onClick={() => goToView('tasks')}>
                                                    <td className={`px-4 sm:px-6 ${tableCellPadding} text-sm font-semibold text-slate-900`}>
                                                        <div className="leading-tight">{task.title || task.name || 'Ohne Titel'}</div>
                                                        <div className="sm:hidden mt-1 text-[11px] font-medium text-slate-500">{task.customerName || task.customer || task.kunde || '–'}</div>
                                                    </td>
                                                    <td className={`hidden sm:table-cell px-4 sm:px-6 ${tableCellPadding} text-xs text-slate-600`}>{task.customerName || task.customer || task.kunde || '–'}</td>
                                                    <td className={`px-4 sm:px-6 ${tableCellPadding} text-xs font-semibold ${overdue ? 'text-red-600' : 'text-slate-600'}`}>{due ? formatDateSafe(due) : '–'}</td>
                                                    <td className={`hidden md:table-cell px-4 sm:px-6 ${tableCellPadding} text-xs`}><IOSBadge color={task.priority === 'high' ? 'red' : task.priority === 'medium' ? 'amber' : 'blue'}>{task.priority === 'high' ? 'Hoch' : task.priority === 'medium' ? 'Mittel' : 'Normal'}</IOSBadge></td>
                                                    <td className={`px-4 sm:px-6 ${tableCellPadding} text-xs`}><IOSBadge color={overdue ? 'red' : (rawStatus === 'in_progress' ? 'amber' : rawStatus === 'done' ? 'green' : 'blue')}>{statusText}</IOSBadge></td>
                                                    <td className={`hidden lg:table-cell px-4 sm:px-6 ${tableCellPadding} text-xs text-slate-600`}>{task.assignedTo || task.owner || '–'}</td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                            {taskRows.length === 0 && (
                                <div className="text-center py-10">
                                    <p className="text-sm text-slate-500 dark:text-slate-300">Keine Aufgaben in dieser Ansicht.</p>
                                    <div className="mt-3 text-xs text-slate-500 dark:text-slate-300 space-y-1">
                                        <p>1. Zeitraum prüfen</p>
                                        <p>2. Filter „Offen / Diese Woche / Überfällig“ wechseln</p>
                                        <p>3. Aufgabenliste öffnen</p>
                                    </div>
                                    <div className="mt-3">
                                        <IOSButton variant="secondary" onClick={() => goToView('tasks')}>Aufgaben öffnen</IOSButton>
                                    </div>
                                </div>
                            )}
                            <div className="pt-4 flex justify-end">
                                <IOSButton variant="secondary" onClick={() => goToView('tasks')}>Aufgaben öffnen</IOSButton>
                            </div>
                            </IOSCard>

                        {/* B2: Vertragsverlängerungen */}
                            <IOSCard
                                title="Vertragsverlängerungen"
                                className="border border-amber-200/60 dark:border-amber-400/20"
                                headerAction={
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <button onClick={() => setRenewalRange(7)} className={`px-3 py-1.5 rounded-full text-xs font-semibold ${renewalRange === 7 ? 'bg-[#FEF3E8] text-amber-700' : 'bg-[#F3F5F9] text-slate-500'}`}>7 Tage</button>
                                        <button onClick={() => setRenewalRange(30)} className={`px-3 py-1.5 rounded-full text-xs font-semibold ${renewalRange === 30 ? 'bg-[#EEF3FF] text-[#2F6BFF]' : 'bg-[#F3F5F9] text-slate-500'}`}>30 Tage</button>
                                        <button onClick={() => setRenewalRange(90)} className={`px-3 py-1.5 rounded-full text-xs font-semibold ${renewalRange === 90 ? 'bg-[#EEF3FF] text-[#2F6BFF]' : 'bg-[#F3F5F9] text-slate-500'}`}>90 Tage</button>
                                    </div>
                                }
                            >
                            <div className="overflow-x-auto -mx-6">
                                <table className="w-full text-left border-collapse">
                                    <thead>
                                        <tr className={`bg-slate-50 border-y border-[#E9ECF2] ${scanMode ? 'sticky top-0 z-10' : ''}`}>
                                            <th className={`px-4 sm:px-6 ${tableHeaderPadding} text-[10px] font-bold uppercase tracking-widest text-slate-400`}>Kunde</th>
                                            <th className={`hidden sm:table-cell px-4 sm:px-6 ${tableHeaderPadding} text-[10px] font-bold uppercase tracking-widest text-slate-400`}>Sparte / Produkt</th>
                                            <th className={`px-4 sm:px-6 ${tableHeaderPadding} text-[10px] font-bold uppercase tracking-widest text-slate-400`}>Verlängerungsdatum</th>
                                            <th className={`hidden md:table-cell px-4 sm:px-6 ${tableHeaderPadding} text-[10px] font-bold uppercase tracking-widest text-slate-400`}>Beitrag</th>
                                            <th className={`px-4 sm:px-6 ${tableHeaderPadding} text-[10px] font-bold uppercase tracking-widest text-slate-400 text-right`}>Status</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-[#E9ECF2]">
                                        {renewalRows.map((item) => (
                                            <tr key={item.id} className={`transition-colors cursor-pointer ${item.daysUntil <= 7 ? 'bg-amber-50/45 hover:bg-amber-50/70' : 'hover:bg-slate-50/50'}`} onClick={() => onSelectCustomer(myCustomers.find((c: any) => c.id === item.customerId))}>
                                                <td className={`px-4 sm:px-6 ${tableCellPadding} text-sm font-bold text-slate-900`}>
                                                    <div className="leading-tight">{item.customerName}</div>
                                                    <div className="sm:hidden mt-1 text-[11px] font-medium text-slate-500">{item.sparte}</div>
                                                </td>
                                                <td className={`hidden sm:table-cell px-4 sm:px-6 ${tableCellPadding} text-xs font-semibold text-slate-500`}>{item.sparte}</td>
                                                <td className={`px-4 sm:px-6 ${tableCellPadding} text-xs font-bold ${item.daysUntil <= 7 ? 'text-amber-700' : 'text-slate-900'}`}>{formatDateSafe(item.ablauf)}</td>
                                                <td className={`hidden md:table-cell px-4 sm:px-6 ${tableCellPadding} text-xs text-slate-600`}>{formatMoneySafe(item.beitrag)}</td>
                                                <td className={`px-4 sm:px-6 ${tableCellPadding} text-right`}>
                                                    <IOSBadge color={item.daysUntil <= 7 ? 'red' : item.daysUntil <= 30 ? 'amber' : 'blue'}>
                                                        {item.daysUntil} Tage
                                                    </IOSBadge>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            {renewalRows.length === 0 && (
                                <div className="text-center py-8">
                                    <p className="text-sm text-slate-500 dark:text-slate-300">Keine anstehenden Abläufe gefunden.</p>
                                    <div className="mt-3 text-xs text-slate-500 dark:text-slate-300 space-y-1">
                                        <p>1. Zeitraum prüfen</p>
                                        <p>2. Verlängerungsfilter anpassen</p>
                                        <p>3. Abläufe-Ansicht öffnen</p>
                                    </div>
                                    <div className="mt-3">
                                        <IOSButton variant="secondary" onClick={() => goToView('expirations')}>Abläufe öffnen</IOSButton>
                                    </div>
                                </div>
                            )}
                            </IOSCard>
                        </div>

                    {/* ZONE C: Sidebar (Right) */}
                        <div className="lg:col-span-4 flex flex-col gap-6">

                            <IOSCard className="border border-cyan-200/60 dark:border-cyan-400/20">
                            <div className="flex flex-col items-center text-center gap-3">
                                <div className="w-20 h-20 rounded-full bg-gradient-to-br from-[#EEF3FF] to-[#F5FAFF] border border-[#E9ECF2] flex items-center justify-center text-2xl font-bold text-[#2F6BFF]">
                                    {currentUser?.name ? currentUser.name[0] : '?'}
                                </div>
                                <div>
                                    <p className="text-lg font-bold text-slate-900">{currentUser?.name || 'Mitarbeiter'}</p>
                                    <p className="text-xs text-slate-500">{currentUser?.role === 'admin' ? 'Administrator' : 'Mitarbeiter'}</p>
                                </div>
                                <div className="grid grid-cols-4 gap-2 w-full">
                                    <button onClick={() => goToView('customers')} className="h-9 rounded-xl bg-slate-100 text-slate-600 text-xs font-semibold hover:bg-slate-200">Kunden</button>
                                    <button onClick={() => goToView('tasks')} className="h-9 rounded-xl bg-slate-100 text-slate-600 text-xs font-semibold hover:bg-slate-200">Aufgaben</button>
                                    <button onClick={() => goToView('phonebook')} className="h-9 rounded-xl bg-slate-100 text-slate-600 text-xs font-semibold hover:bg-slate-200">Anruf</button>
                                    <button onClick={() => goToView('settings')} className="h-9 rounded-xl bg-slate-100 text-slate-600 text-xs font-semibold hover:bg-slate-200">Einstellungen</button>
                                </div>
                            </div>
                            </IOSCard>

                        {/* C1: Zielerreichung */}
                            {currentGoal && (
                            <IOSCard title={`Ziel ${currentGoal.year}`} className="border border-emerald-200/60 dark:border-emerald-400/20">
                                <div className="space-y-5">
                                    <div className="flex items-end justify-between">
                                        <span className="text-3xl font-bold tracking-tight text-slate-900">{goalPercentage}%</span>
                                        <span className="text-xs font-medium text-slate-400">{currentGoal.current.toLocaleString()} / {currentGoal.target.toLocaleString()} WS</span>
                                    </div>
                                    <div className="h-3 w-full bg-slate-100 rounded-full overflow-hidden">
                                        <div
                                            className="h-full bg-[#2F6BFF] rounded-full transition-all duration-1000"
                                            style={{ width: `${Math.min(100, goalPercentage)}%` }}
                                        />
                                    </div>
                                    <p className="text-xs text-slate-500 leading-relaxed italic">{currentGoal.description}</p>
                                </div>
                                </IOSCard>
                            )}

                        {/* C2: Heute (Termine / Dienstplan) */}
                            <IOSCard title="Heute" className="border border-indigo-200/60 dark:border-indigo-400/20">
                            <div className="space-y-4">
                                {todayShiftRows.map((shift: any, i: number) => (
                                    <div key={i} className="flex items-center gap-4 p-3 bg-indigo-50/30 rounded-xl border border-indigo-100/50">
                                        <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center text-indigo-600">
                                            <Clock size={20} />
                                        </div>
                                        <div className="flex flex-col">
                                            <span className="text-sm font-bold text-slate-900">{shift.userName || 'Mitarbeiter'}</span>
                                            <span className="text-[10px] font-black uppercase text-indigo-500">{shift.start} - {shift.end}</span>
                                        </div>
                                    </div>
                                ))}
                                {todayShiftRows.length === 0 && (
                                    <div className="text-center py-6">
                                        <p className="text-xs italic text-slate-500 dark:text-slate-300">Keine Schichten für heute geplant.</p>
                                        <div className="mt-3">
                                            <IOSButton variant="secondary" className="text-xs px-3 py-2" onClick={() => goToView('roster')}>Dienstplan öffnen</IOSButton>
                                        </div>
                                    </div>
                                )}
                            </div>
                            </IOSCard>

                        {/* C3: Kundenaktivität (Geburtstage) */}
                            {upcomingBirthdays.length > 0 && (
                                <IOSCard title="Anstehende Geburtstage" className="border border-amber-200/60 dark:border-amber-400/20">
                                <div className="space-y-4">
                                    {upcomingBirthdays.slice(0, 4).map((c: any) => {
                                        const bDay = getNextBirthdayDate(c.gebDatum);
                                        const daysUntil = Math.ceil((bDay.getTime() - new Date().getTime()) / 86400000);
                                        return (
                                            <div key={c.id} onClick={() => onSelectCustomer(c)} className="flex items-center justify-between p-3 hover:bg-slate-50 rounded-xl transition-colors cursor-pointer group">
                                                <div className="flex items-center gap-3">
                                                    <div className="w-9 h-9 rounded-full bg-amber-100 flex items-center justify-center text-amber-600 group-hover:scale-110 transition-transform">
                                                        <Cake size={18} />
                                                    </div>
                                                    <div className="flex flex-col">
                                                        <span className="text-sm font-bold text-slate-900">{c.vorname} {c.name}</span>
                                                        <span className="text-[10px] font-medium text-slate-400">{c.gebDatum}</span>
                                                    </div>
                                                </div>
                                                <IOSBadge color={daysUntil === 0 ? 'green' : 'amber'}>
                                                    {daysUntil === 0 ? 'Heute' : `in ${daysUntil}T`}
                                                </IOSBadge>
                                            </div>
                                        );
                                    })}
                                </div>
                                </IOSCard>
                            )}
                        </div>
                    </div>

                    <div className="fixed right-4 bottom-4 z-30 hidden md:flex flex-col gap-2">
                        <button
                            onClick={() => goToView('tasks')}
                            className="rounded-xl border border-blue-200 bg-blue-50 px-3 py-2 text-xs font-semibold text-blue-700 shadow-sm transition-colors hover:bg-blue-100 dark:border-blue-400/30 dark:bg-blue-500/15 dark:text-blue-200"
                        >
                            Aufgabe öffnen
                        </button>
                        <button
                            onClick={() => goToView('phonebook')}
                            className="rounded-xl border border-cyan-200 bg-cyan-50 px-3 py-2 text-xs font-semibold text-cyan-700 shadow-sm transition-colors hover:bg-cyan-100 dark:border-cyan-400/30 dark:bg-cyan-500/15 dark:text-cyan-200"
                        >
                            Anruf starten
                        </button>
                        <button
                            onClick={() => goToView('expirations')}
                            className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs font-semibold text-amber-700 shadow-sm transition-colors hover:bg-amber-100 dark:border-amber-400/30 dark:bg-amber-500/15 dark:text-amber-200"
                        >
                            Abläufe prüfen
                        </button>
                    </div>
                </div>
            </div>
            {/* Kundenliste oder Fallback */}
            {adminView === 'employee' && myCustomers.length === 0 && (
                <div className="text-center text-sm text-gray-500 mt-8">
                    <p>Keine eigenen Kunden vorhanden.</p>
                    <div className="mt-2 text-xs text-slate-500 space-y-1">
                        <p>1. Sichtbarkeiten im Team prüfen</p>
                        <p>2. Filter zurücksetzen</p>
                        <p>3. Kundenliste öffnen</p>
                    </div>
                </div>
            )}
            {showBestandsModal && <BestandsModal customers={myCustomers} onClose={() => setShowBestandsModal(false)} />}
            {showWidgetModal && onSaveWidgetOrder && (
                <div role="dialog" aria-modal="true" aria-labelledby="widget-modal-title" className={`fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-4 sm:p-6 bg-slate-900/20 backdrop-blur-md shadow-2xl`}>
                    <div className={`rounded-t-2xl sm:rounded-2xl max-w-sm w-full max-h-[90vh] flex flex-col app-glass-panel text-card-foreground overflow-hidden shadow-2xl`}>
                        <div className={`flex items-center justify-between p-4 sm:p-6 pb-2 sm:pb-4 flex-shrink-0 border-b ${isDark ? 'border-slate-700/50' : 'border-slate-100'}`}>
                            <h3 id="widget-modal-title" className="text-lg font-bold">Dashboard anpassen</h3>
                            <button type="button" onClick={() => setShowWidgetModal(false)} aria-label="Schließen" className={`min-w-[44px] min-h-[44px] flex items-center justify-center rounded-xl hover:bg-slate-50`}><X size={20} /></button>
                        </div>
                        <p className={`text-sm px-4 sm:px-6 mb-2 text-muted-foreground`}>Widgets in der gewünschten Reihenfolge anordnen:</p>
                        <ul className="space-y-1 px-4 sm:px-6 overflow-y-auto flex-1 min-h-0">
                            {editOrder.map((id, idx) => (
                                <li key={id} className={`flex items-center justify-between rounded-xl px-4 py-3 min-h-[52px] bg-slate-50`}>
                                    <span className="font-medium truncate pr-2">{WIDGET_LABELS[id] || id}</span>
                                    <span className="flex gap-1 flex-shrink-0">
                                        <button type="button" disabled={idx === 0} aria-label="Nach oben" onClick={() => setEditOrder(prev => { const n = [...prev]; const t = n[idx]; n[idx] = n[idx - 1]; n[idx - 1] = t; return n; })} className={`min-w-[44px] min-h-[44px] flex items-center justify-center rounded-lg hover:bg-slate-200 disabled:opacity-30`}><ChevronUp size={18} /></button>
                                        <button type="button" disabled={idx === editOrder.length - 1} aria-label="Nach unten" onClick={() => setEditOrder(prev => { const n = [...prev]; const t = n[idx]; n[idx] = n[idx + 1]; n[idx + 1] = t; return n; })} className={`min-w-[44px] min-h-[44px] flex items-center justify-center rounded-lg hover:bg-slate-200 disabled:opacity-30`}><ChevronDown size={18} /></button>
                                    </span>
                                </li>
                            ))}
                        </ul>
                        <div className="flex gap-3 p-4 sm:p-6 pt-4 pb-6 flex-shrink-0">
                            <button type="button" onClick={() => setShowWidgetModal(false)} className={`flex-1 min-h-[44px] py-2.5 rounded-xl font-semibold transition-colors ${isDark ? 'bg-white/10 hover:bg-white/20' : 'bg-slate-100 hover:bg-slate-200'}`}>Abbrechen</button>
                            <button type="button" onClick={() => { onSaveWidgetOrder(editOrder); setShowWidgetModal(false); }} className={`flex-1 min-h-[44px] py-2.5 rounded-xl font-semibold transition-colors ${isDark ? 'bg-cyan-500/30 text-cyan-200 hover:bg-cyan-500/50' : 'bg-cyan-600 text-white hover:bg-cyan-700'}`}>Speichern</button>
                        </div>
                    </div>
                </div>
            )}
        </PageLayout>
    );
});
